/**
 * Centralized formatters. Use these everywhere — never instantiate `Intl.*`
 * inline.
 *
 * `price` = property prices (integers, no decimals): "289 000 €".
 * `priceWithCents` = subscription prices (always 2 decimals): "7,59 €".
 */
export const price = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

/** Compact price for map pills: 289000 → "289 k€", 1250000 → "1,3 M€" (one decimal). */
export function priceCompact(value: number): string {
  if (value >= 1_000_000) {
    const m = Math.round(value / 100_000) / 10
    return `${m.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M€`
  }
  return `${Math.round(value / 1000).toLocaleString('fr-FR')} k€`
}

/** Property price, or the "Non estimé" sentinel when the back returned no estimate (null). */
export function priceOrUnknown(value: number | null): string {
  return value == null ? 'Non estimé' : price.format(value)
}

export const priceWithCents = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const pricePerSquareMeter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

export const date = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
})

export const dateLong = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

export const dateTimeLong = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})

export const integer = new Intl.NumberFormat('fr-FR')

/** Safe long date: returns `null` when the value is missing or unparseable. */
export function formatDateLong(value: string | number | Date | null | undefined): string | null {
  if (value == null) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : dateLong.format(d)
}

/** Safe long date+time: returns `null` when the value is missing or unparseable. */
export function formatDateTimeLong(value: string | number | Date | null | undefined): string | null {
  if (value == null) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : dateTimeLong.format(d)
}

/** Compact relative "reçu" label, matching the POC (2 h, hier, 3 j…). */
export function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const h = diff / 3_600_000
  if (h < 1) return "à l'instant"
  if (h < 24) return `${Math.round(h)} h`
  const d = Math.round(h / 24)
  if (d === 1) return 'hier'
  if (d < 7) return `${d} j`
  const w = Math.round(d / 7)
  if (w < 5) return `${w} sem.`
  return `${Math.round(d / 30)} mois`
}

/** Capitalise the first letter (e.g. backend lowercase enums like `appartement`). */
export function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
